/**
 * `define*` helpers for the per-feature extension files (see ./types). They are identity functions that pin the
 * shape at compile time and tag the value, so the loader can tell definitions apart from other exports.
 */
import type { TestId } from '@quadratic/testids';
import type { LucideIcon } from '@quadratic/ui';
import type { ComponentType } from 'react';
import type { SlotContexts, SlotId } from '../slots/catalog';
import type { CommandDef, NavEntry, PaletteSourceDef, PropertyDef, SettingsPageDef, SweepStateDef } from './types';

const KIND = Symbol.for('quadratic.extension');

export type Kind = 'command' | 'nav' | 'settings' | 'property' | 'paletteSource' | 'sweepState' | 'slot';

type Tagged<T, K extends Kind> = T & { readonly [KIND]: K };

const tag = <T extends object, K extends Kind>(kind: K, v: T): Tagged<T, K> => ({ ...v, [KIND]: kind });

export type Command = Tagged<CommandDef, 'command'>;
export type Nav = Tagged<NavEntry, 'nav'>;
export type SettingsPage = Tagged<SettingsPageDef, 'settings'>;
export type Property = Tagged<PropertyDef, 'property'>;
export type PaletteSource = Tagged<PaletteSourceDef, 'paletteSource'>;
export type SweepState = Tagged<SweepStateDef, 'sweepState'>;

export const defineCommand = (d: CommandDef): Command => tag('command', d);
export const defineNav = (d: NavEntry): Nav => tag('nav', d);
export const defineSettingsPage = (d: SettingsPageDef): SettingsPage => tag('settings', d);
export const defineProperty = (d: PropertyDef): Property => tag('property', d);
export const definePaletteSource = (d: PaletteSourceDef): PaletteSource => tag('paletteSource', d);
export const defineSweepState = (d: SweepStateDef): SweepState => tag('sweepState', d);

/** One contribution to a host slot (app/slots/catalog.ts). */
export interface SlotContributionDef<Id extends SlotId> {
  readonly slot: Id;
  /** Unique within the slot ('sub-issues', 'relations'). */
  readonly id: string;
  /** Ascending; hosts render contributions in this order (default 100). */
  readonly order?: number;
  /** Renders only when true (renderer slots match on `type` / `kind`, layouts on `layout`). */
  match?(ctx: SlotContexts[Id]): boolean;
  readonly component: ComponentType<SlotContexts[Id]>;
  /** For tab, layout and toolbar slots: the visible label, the value it selects, its icon and test id. */
  readonly label?: string;
  readonly value?: string;
  readonly icon?: LucideIcon;
  readonly testId?: TestId;
  /** view.layouts: the models a layout renders. */
  readonly models?: readonly ('issue' | 'project')[];
}

export type SlotContribution<Id extends SlotId = SlotId> = Tagged<SlotContributionDef<Id>, 'slot'>;

export const defineSlot = <Id extends SlotId>(d: SlotContributionDef<Id>): SlotContribution<Id> => tag('slot', d);

/**
 * The assembled registries (built by `load.ts`, held by `current.ts`). Declared here, in the type-only leaf, so
 * `current.ts` needs no import of `load.ts`: that edge closed the cycle current → load → __generated__/extensions →
 * features/* → app/public → … → current (dependency-cruiser no-circular; W1 step 0).
 */
export interface Registries {
  readonly commands: readonly Command[];
  readonly nav: readonly Nav[];
  readonly settings: readonly SettingsPage[];
  readonly properties: readonly Property[];
  readonly paletteSources: readonly PaletteSource[];
  readonly sweepStates: readonly SweepState[];
  readonly slots: ReadonlyMap<SlotId, readonly SlotContribution[]>;
}

export function kindOf(v: unknown): Kind | null {
  if (typeof v !== 'object' || v === null) return null;
  const k = (v as { [KIND]?: unknown })[KIND];
  return typeof k === 'string' ? (k as Kind) : null;
}

/** A command's key bindings: `bindings`, else `keys` in `scope` (default global). */
export function bindingsOf(cmd: { keys?: readonly string[]; scope?: string; bindings?: readonly { keys: readonly string[]; scope: string }[] }): { keys: readonly string[]; scopes: string[] }[] {
  const list = cmd.bindings ?? (cmd.keys && cmd.keys.length ? [{ keys: cmd.keys, scope: cmd.scope ?? 'global' }] : []);
  return list.map((b) => ({ keys: b.keys, scopes: b.scope.split('|').map((s) => s.trim()) }));
}

/**
 * The key hint to show for a command in a context (palette rows, context menus): the first key of the binding in the
 * deepest active scope that has one (`scopes` deepest first, as CommandContext.scopes), so `app.toggle-details` reads
 * Cmd/Ctrl+I on a project page and ] elsewhere. A command bound in no active scope shows its first key anywhere.
 */
export function shortcutFor(cmd: Parameters<typeof bindingsOf>[0], scopes: readonly string[]): string | undefined {
  const bindings = bindingsOf(cmd);
  for (const s of scopes) {
    const b = bindings.find((x) => x.scopes.includes(s) && x.keys.length > 0);
    if (b) return b.keys[0];
  }
  return keysOf(cmd)[0];
}

/** Every key sequence a command binds, in any scope. */
export function keysOf(cmd: Parameters<typeof bindingsOf>[0]): string[] {
  return [...new Set(bindingsOf(cmd).flatMap((b) => b.keys))];
}
